import { useRef, useState } from "react";
import { Plus } from "lucide-react";
import {
  ArrowDown,
  ArrowDownLight,
  PinIconFilled,
  PinIconLight,
} from "../../../assets";
import logger from "../../../services/Logger";
import spaceStore from "../../../store/spaceStore";
import { useMoveChatAPI } from "../../../api/queryHooks";
import UserAuthStore from "../../../store/userStore";
import { v4 as uuidv4 } from "uuid";
import { useLocation, useNavigate } from "react-router-dom";
import { getNavigateLink, isChatActive } from "../../../utils/helpers";
import useGenerateApiSignal from "../../../store/useGenerateApiSignal";
import SparkSpectraListing from "./SparkSpectraListing";
import { PERMISSIONS } from "../../../utils/permissions";
import useSessionIdStore from "../../../store/useSessionIdStore";
import {
  FileStore,
  matchPendingUploads,
  SpectraUploadStore,
} from "../../../store/fileUploadStore";
import ChatStore from "../../../store/chatStore";
import UIStore from "../../../store/uiStore";
import { pinSpectraAPI } from "../../../utils/spectraApi/pinSpectra";
import useSpectraDeleteModalStore from "../../../store/useSpectraDeleteModal";

const NewSparkButtonForSpectra = ({ space }) => {
  const navigate = useNavigate();
  const addChatDisabled = ChatStore((state) => state.addChatDisabled);
  const setShowSidebar = UIStore((state) => state.setShowSidebar);

  const handleClick = () => {
    if (!PERMISSIONS.CREATE_NEW_SPARK_IN_SPECTRA(space)) return;
    if (addChatDisabled) return;

    // Immediately disable the button to prevent rapid clicks
    ChatStore.getState().setAddChatDisabled(true);

    try {
      FileStore.getState().setFiles([]);
      FileStore.getState().setNewFiles([]);
      FileStore.getState().setFileLoading(false);
      useGenerateApiSignal.getState().signal?.abort();

      // Navigate with a new chat_id so MainContent renders immediately
      navigate(`/spectra/${space.space_id}/chat/${uuidv4()}`);
      
      // Close sidebar on mobile/tablet after navigation
      if (window.innerWidth < 940) {
        setShowSidebar(false);
      }

      // Re-enable the button after a short delay as a failsafe
      setTimeout(() => {
        ChatStore.getState().setAddChatDisabled(false);
      }, 1000);
    } catch (error) {
      logger.error('SpectraAccordian', 'Error in NewSparkButtonForSpectra handleClick', { error: error.message, space_id: space.space_id });
      // Re-enable the button if there's an error
      ChatStore.getState().setAddChatDisabled(false);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`flex-shrink-0 text-xs text-ink-primary dark:text-ink-accent flex items-center gap-[5px] opacity-0 transition-opacity duration-300 ${
        PERMISSIONS.CREATE_NEW_SPARK_IN_SPECTRA(space)
          ? "group-hover:opacity-100"
          : "group-hover:opacity-50"
      }`}
      title={
        PERMISSIONS.CREATE_NEW_SPARK_IN_SPECTRA(space)
          ? ""
          : "You don't have permission to create new spark in spectra."
      }
    >
      <Plus size={14} className="shrink-0" aria-hidden="true" />
      New Spark
    </div>
  );
};

const SpectraAccordian = ({ space }) => {
  // Granular selector — only re-renders THIS accordion when its own open state changes,
  // not when any other accordion opens/closes.
  const isOpen = spaceStore((state) => state.openSpectraId === space.space_id);
  const setOpenSpectraId = spaceStore((state) => state.setOpenSpectraId);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const resumeInputRef = useRef(null);
  const user = UserAuthStore((state) => state.user);
  const orgId = UserAuthStore((state) => state.orgId);
  const { mutate: moveChatMutate } = useMoveChatAPI();
  const location = useLocation();
  const navigate = useNavigate();
  const setShowSidebar = UIStore((state) => state.setShowSidebar);
  const openDeleteModal = useSpectraDeleteModalStore((state) => state.setIsOpen);
  const localUpload = SpectraUploadStore(
    (state) => state.uploads[space.space_id]
  );
  const lifecycleStatus = localUpload?.status || space.status || "READY";
  const isReady = lifecycleStatus === "READY";
  const serverUploads = space.uploads || {};
  const hasPendingServerUploads = Object.values(serverUploads).some(
    (upload) => upload.status !== "UPLOADED"
  );

  const handleRetry = async (event) => {
    event.stopPropagation();
    try {
      await SpectraUploadStore.getState().retry(space.space_id);
    } catch (error) {
      logger.error('SpectraAccordian', 'Spectra upload retry failed', {
        error: error.message,
        spaceId: space.space_id,
      });
    }
  };

  const handleResumeSelection = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";
    const files = matchPendingUploads(selectedFiles, serverUploads);
    if (!files) {
      logger.error('SpectraAccordian', 'Reselected files do not match pending uploads', {
        spaceId: space.space_id,
      });
      return;
    }
    try {
      await SpectraUploadStore.getState().start({
        spaceId: space.space_id,
        files,
        finalizeUploadIds: Object.keys(serverUploads),
      });
    } catch (error) {
      logger.error('SpectraAccordian', 'Resumed Spectra upload failed', {
        error: error.message,
        spaceId: space.space_id,
      });
    }
  };

  const handleClickOpen = () => {
    if (!isReady) return;
    setOpenSpectraId(isOpen ? null : space.space_id);
  };

  const handleOpenViewer = () => {
    if (!isReady) return;
    navigate(`/spectra/${space.space_id}`);
    // Close sidebar on mobile/tablet after navigation
    if (window.innerWidth < 940) {
      setShowSidebar(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!PERMISSIONS.MOVE_SPARK_IN_SPECTRA(space)) return;
    e.dataTransfer.dropEffect = "move";
    setIsDraggingOver(true);
  };

  const handleDragLeave = () => {
    if (!PERMISSIONS.MOVE_SPARK_IN_SPECTRA(space)) return;
    setIsDraggingOver(false);
  };

  const handleDrop = (e) => {
    if (!PERMISSIONS.MOVE_SPARK_IN_SPECTRA(space)) return;
    e.preventDefault();
    setIsDraggingOver(false);

    const chat = JSON.parse(e.dataTransfer.getData("chat"));
    const fromSpace = e.dataTransfer.getData("space")
      ? JSON.parse(e.dataTransfer.getData("space"))
      : undefined;

    if (fromSpace?.space_id === space.space_id) return;

    if (fromSpace) {
      // Spectra -> Spectra
      moveChatMutate({
        new_sid: space.space_id,
        old_sid: fromSpace.space_id,
        chat_ids: [chat.uid],
        chat_data: [chat],
        org_id: orgId,
        uid: user.uid,
        session_id: useSessionIdStore.getState().session_id,
      });
      if (isChatActive(location, chat.uid, fromSpace))
        navigate(getNavigateLink(chat.uid, space));
    } else {
      // Personal -> Spectra
      moveChatMutate({
        new_sid: space.space_id,
        chat_ids: [chat.uid],
        chat_data: [chat],
        org_id: orgId,
        uid: user.uid,
        session_id: useSessionIdStore.getState().session_id,
      });
      if (isChatActive(location, chat.uid))
        navigate(getNavigateLink(chat.uid, space));
    }

    setOpenSpectraId(space.space_id);
  };

  // Highlight the accordion title when the user is currently viewing any
  // sub-spark belonging to this spectra so the header stays visually "active".
  const isCurrentSpace = location.pathname.startsWith(`/spectra/${space.space_id}/`);

  // Number of sparks in this spectra. `chat_ids` is the authoritative membership
  // array stored on the space doc (same source the sparks endpoint reads), so its
  // length is an accurate count without an extra fetch. Drives the (N) badge and
  // the dimmed-when-empty arrow.
  const sparkCount = Array.isArray(space.chat_ids) ? space.chat_ids.length : 0;

  return (
    <div
      className={`mb-[0px] ${isReady ? "" : "opacity-50"}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      id={`spectra-${space.space_id}`}
    >
      <div className="flex items-center justify-between cursor-pointer gap-[10px] group pb-2">
        <div
          className="flex-grow flex items-center gap-[5px] truncate min-w-0"
        >
          <button
            type="button"
            onClick={handleClickOpen}
            className={`-ml-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md transition-colors hover:bg-fill-selection-surface dark:hover:bg-fill-gray-700 ${
              sparkCount === 0 ? "opacity-30" : "opacity-100"
            }`}
            aria-label={`${isOpen ? "Collapse" : "Expand"} ${space.title}`}
            aria-expanded={isOpen}
          >
            <span className={`transition-transform duration-300 origin-center ${
              isOpen || isDraggingOver ? "rotate-0" : "-rotate-90"
            }`}>
              <ArrowDownLight className="hidden dark:block w-[10px] h-[6px]" />
              <ArrowDown className="block dark:hidden w-[10px] h-[6px]" />
            </span>
          </button>
          <span 
            className={`text-[13px] font-semibold truncate transition-colors ${
              isCurrentSpace
                ? "text-ink-primary dark:text-ink-accent"
                : "dark:text-ink-white hover:text-ink-primary dark:hover:text-ink-accent"
            }`}
            onClick={handleOpenViewer}
          >
            {space.title}
          </span>
          {!isReady && (
            <span className="flex-shrink-0 text-[10px] text-ink-hex-64748b dark:text-ink-hex-aaaaaa">
              {lifecycleStatus === "CREATE_FAILED" ? "Failed" : "Preparing"}
            </span>
          )}
          {sparkCount > 0 && (
            <span className="flex-shrink-0 text-[11px] tabular-nums text-ink-hex-64748b dark:text-ink-hex-aaaaaa">
              ({sparkCount})
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {lifecycleStatus === "CREATE_FAILED" && localUpload && (
            <button
              type="button"
              className="text-[11px] text-ink-primary dark:text-ink-accent"
              onClick={handleRetry}
            >
              Retry
            </button>
          )}
          {!isReady && !localUpload && hasPendingServerUploads && (
            <button
              type="button"
              className="text-[11px] text-ink-primary dark:text-ink-accent"
              onClick={(event) => {
                event.stopPropagation();
                resumeInputRef.current?.click();
              }}
            >
              Resume
            </button>
          )}
          {!isReady && (
            <button
              type="button"
              className="text-[11px] text-ink-danger-ink dark:text-ink-danger-ink-dark"
              onClick={(event) => {
                event.stopPropagation();
                openDeleteModal(true, space);
              }}
            >
              Delete
            </button>
          )}
          <input
            ref={resumeInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleResumeSelection}
          />
          {/* Pin toggle — filled icon always visible when pinned; outline visible on hover */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              pinSpectraAPI(space.space_id, space.owner_id, !space.pinned);
            }}
            className={`flex items-center justify-center w-5 h-5 rounded transition-opacity duration-200 ${
              space.pinned
                ? "opacity-100"
                : "opacity-0 group-hover:opacity-100"
            }`}
            title={space.pinned ? "Unpin" : "Pin to top"}
            aria-label={space.pinned ? "Unpin spectra" : "Pin spectra to top"}
          >
            {space.pinned ? (
              <PinIconFilled className="w-3 h-3 pin fill-ink-primary dark:fill-ink-accent" />
            ) : (
              <PinIconLight className="w-3 h-3 fill-ink-gray-700 dark:fill-ink-gray-200" />
            )}
          </button>
          <NewSparkButtonForSpectra space={space} />
        </div>
      </div>
      {isOpen || isDraggingOver ? (
          <SparkSpectraListing space={space} />
        ) : null}
    </div>
  );
};

export default SpectraAccordian;
